<template>
  <div class="collections-table-partial-view">
    <div v-if="biobanksStore.waiting" class="status-text text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading collections...</p>
    </div>

    <div v-else-if="filteredCollections.length">
      <div class="d-flex mb-4 justify-content-between align-items-center">
        <div class="result-header">
          <h5>Collections <span class="badge bg-secondary">{{ filteredCollections.length }}</span></h5>
        </div>
        <div class="pagination-controls">
          <nav aria-label="Collections pagination">
            <ul class="pagination pagination-sm mb-0">
              <li :class="['page-item', { disabled: currentPage === 1 }]">
                <a class="page-link" href="#" @click.prevent="previousPage">Previous</a>
              </li>
              <li class="page-item disabled">
                <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
              </li>
              <li :class="['page-item', { disabled: currentPage === totalPages }]">
                <a class="page-link" href="#" @click.prevent="nextPage">Next</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <CollectionsTable
        :collections="paginatedCollections"
        :biobanks="biobanksStore.biobankLookup"
        :show-sample-count="true"
        @row-click="handleCollectionSelect"
      />

      <div class="d-flex justify-content-between align-items-center mt-4">
        <small class="text-muted">Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredCollections.length }}</small>
        <nav aria-label="Collections pagination bottom">
          <ul class="pagination pagination-sm mb-0">
            <li :class="['page-item', { disabled: currentPage === 1 }]">
              <a class="page-link" href="#" @click.prevent="previousPage">Previous</a>
            </li>
            <li class="page-item disabled">
              <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
            </li>
            <li :class="['page-item', { disabled: currentPage === totalPages }]">
              <a class="page-link" href="#" @click.prevent="nextPage">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div v-else class="status-text text-center">
      <h4>{{ noResultsText }}</h4>
      <p class="text-muted">Try adjusting your filters</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useBiobanksStore } from '../../stores/biobanksStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useFiltersStore } from '../../stores/filtersStore';
import CollectionsTable from '../tables/CollectionsTable.vue';

const biobanksStore = useBiobanksStore();
const settingsStore = useSettingsStore();
const filtersStore = useFiltersStore();
const router = useRouter();

const currentPage = ref(1);
const pageSize = ref(20);

const filteredCollections = computed(() => {
  if (!biobanksStore.collectionsForDisplay) {
    return [];
  }
  
  // Apply any active filters here if needed
  // For now, return all collections
  return biobanksStore.collectionsForDisplay;
});

const totalPages = computed(() => {
  return Math.ceil(filteredCollections.value.length / pageSize.value);
});

const startIndex = computed(() => {
  return (currentPage.value - 1) * pageSize.value;
});

const endIndex = computed(() => {
  const end = currentPage.value * pageSize.value;
  return Math.min(end, filteredCollections.value.length);
});

const paginatedCollections = computed(() => {
  return filteredCollections.value.slice(startIndex.value, endIndex.value);
});

const noResultsText = computed(() => {
  if (filtersStore.bookmarkWaitingForApplication) {
    return 'Loading...';
  }
  return 'No collections were found';
});

const handleCollectionSelect = (collection: any) => {
  router.push({ name: "collectiondetails", params: { id: collection.id } });
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Initialize collections on mount
onMounted(() => {
  biobanksStore.getAllCollections();
});

// Reset pagination when collections change
watch(() => filteredCollections.value.length, () => {
  currentPage.value = 1;
});
</script>

<style scoped>
.collections-table-partial-view {
  width: 100%;
}

.status-text {
  padding: 2rem;
  color: #999;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-header h5 {
  margin: 0;
  color: #333;
}

.badge {
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
}

.pagination-controls {
  display: flex;
  gap: 1rem;
}

.pagination {
  margin: 0;
}

.page-link {
  cursor: pointer;
  color: #2196f3;
  border: 1px solid #dee2e6;
}

.page-link:hover {
  background-color: #f8f9fa;
  color: #1976d2;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
  color: #999;
  background-color: #f8f9fa;
}

.page-item.active .page-link {
  background-color: #2196f3;
  border-color: #2196f3;
  color: white;
}

.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  color: #2196f3;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pagination-controls {
    display: none;
  }
  
  .d-flex {
    flex-direction: column;
    gap: 1rem;
  }
  
  .result-header {
    margin-bottom: 1rem;
  }
}
</style>
